// V2 content-fit measurement — the "is this slide overpacked?" signal, measured
// in a real browser DOM.
//
// A later check/correct pass uses this to detect slides whose content overflows
// the 1920×1080 design frame badly enough to hurt readability, and revise them.
//
// The metric is identical to the eval harness's render-fit stage
// (`web/scripts/eval-render.mjs`): read a rendered `.slide`'s CONTENT-FIT ratio
// (clientHeight/scrollHeight) in the design frame. Unlike the eval harness — which
// drives the slide-stage PRESENTER in headless Chromium — the Studio has NO canvas
// iframe; it renders the focus slide into light DOM. So we measure the same way:
// render each slide into ONE hidden light-DOM host appended to document.body. That
// host inherits the global `slides.css` / `tokens.css` loaded by index.html, so the
// rendered `.slide` lays out exactly as it does in the Studio canvas — no iframe, no
// postMessage. Transforms (the viewport scale-to-fit) do not affect clientHeight /
// scrollHeight, so the content-fit ratio is read in design space directly.

import { renderSlide } from './render-slides';
import type { SlideDeck } from './slide-model';

/** A single slide's fit measurement. `scale` is the content-fit ratio
 *  (1 = the content fits the 1080 design frame; lower = it overflows by that
 *  ratio); `overflow` means the content exceeds the frame. Mirrors the V1
 *  FitSlide shape at create-page.ts (which reported it via postMessage). */
export interface FitSlide {
  index: number;
  label: string;
  scale: number;
  overflow: boolean;
}

/** A slide whose content-fit scale is below this is "overpacked" — small enough
 *  to hurt readability and worth an automatic revise. Matches the eval harness's
 *  OVERPACK_THRESHOLD (`web/scripts/eval-render.mjs`) and eval.OverpackThreshold. */
export const OVERPACK_THRESHOLD = 0.75;

/** The 1920×1080 design frame the slide markup is authored against. */
const DESIGN_W = 1920;
const DESIGN_H = 1080;

/** Promise-based delay. */
const delay = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

/**
 * Measure every slide's content-fit in a hidden light-DOM host.
 *
 * For each slide:
 *  1. render it into ONE reused hidden host sized to the design frame,
 *  2. read the `.slide`'s content-fit ratio exactly like eval-render.mjs,
 *  3. report `{ index, label, scale, overflow }`.
 *
 * Returns `[]` in a non-DOM context (SSR / node test runner) so it never throws.
 * NB: true height measurement needs a real browser — jsdom returns 0 for
 * clientHeight / scrollHeight — so the metric itself is verified by the Playwright
 * check, not the node unit tests.
 */
export async function measureFit(deck: SlideDeck): Promise<FitSlide[]> {
  // Guard: no DOM (SSR / vitest `node` environment) → never throw.
  if (typeof document === 'undefined' || typeof window === 'undefined') return [];
  const slides = deck?.slides ?? [];
  if (slides.length === 0) return [];

  const overrides = deck.overrides ?? {};
  const regionStyles = deck.regionStyles ?? {};

  // ONE hidden host, off-screen, sized to the 1920×1080 design space so the
  // rendered .slide lays out at its authored dimensions and inherits the global
  // slides.css / tokens.css. overflow:hidden + visibility:hidden keep it both
  // invisible and non-interactive; pointer-events:none belt-and-braces.
  const host = document.createElement('div');
  host.setAttribute('aria-hidden', 'true');
  host.style.cssText =
    `position:absolute; left:-99999px; top:0; width:${DESIGN_W}px; height:${DESIGN_H}px;` +
    ' overflow:hidden; pointer-events:none; visibility:hidden';
  document.body.appendChild(host);

  const out: FitSlide[] = [];
  try {
    // Web fonts change wrap heights, so wait for them once before the loop and
    // add a small settle before the FIRST read (eval-render waits too).
    if (document.fonts && typeof document.fonts.ready?.then === 'function') {
      await document.fonts.ready;
    }
    await delay(120);

    for (let index = 0; index < slides.length; index++) {
      const slide = slides[index];
      // Match the Studio's renderSlide call (studio-page.ts): per-slide overrides
      // + region styles + the deck meta.
      host.innerHTML = renderSlide(
        slide,
        overrides[slide.id] || {},
        deck.meta,
        regionStyles[slide.id] || {},
      );
      const el = host.querySelector('.slide') as HTMLElement | null;
      // Content-fit read — identical to eval-render.mjs:118-120.
      const ch = (el?.clientHeight ?? 0) || DESIGN_H;
      const sh = Math.max(el?.scrollHeight ?? 0, ch);
      const scale = Math.min(1, ch / sh);
      const overflow = (sh - ch) > 2;
      out.push({ index, label: slide.label, scale: +scale.toFixed(3), overflow });
    }
  } finally {
    host.remove();
  }
  return out;
}
